import {
  confirmSignUp as cognitoConfirmSignUp,
  resendSignUp as cognitoResendSignUp,
  forgotPassword as cognitoForgotPassword,
  confirmPassword as cognitoConfirmPassword,
} from '../../../adapters/cognito/cognito';
import { makeSecureRequest } from '../../../adapters/api-client/api';
import { saveTokens, clearTokens } from '../../../adapters/api-client/tokenStorage';

export interface LoginCredentials {
  email: string;
  password: string;
}

export type RegisterRole = 'BUYER' | 'SELLER_PRIVATE' | 'SELLER_DEALER';

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: RegisterRole;
  /** Terms of Service acceptance — required by the backend RegisterDto. */
  acceptedTerms: boolean;
  /** Privacy Policy acceptance — required by the backend RegisterDto. */
  acceptedPrivacy: boolean;
}

/**
 * Auth service with Cognito + HMAC integration
 */
export const authService = {
  /**
   * Login via backend API (credentials proxied through api.car-ia.com, not sent directly to Cognito)
   */
  login: async (credentials: LoginCredentials) => {
    try {
      const response = await makeSecureRequest<{
        accessToken: string;
        idToken: string;
        refreshToken: string;
        expiresIn: number;
        user: { userId: string; email: string; role: string; firstName: string; lastName: string };
      }>({
        method: 'POST',
        path: '/api/v1/auth/login',
        body: credentials,
        requiresAuth: false,
        requiresHmac: false,
      });

      saveTokens({
        idToken: response.idToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
      });

      return {
        user: {
          id: response.user.userId,
          email: response.user.email,
          role: response.user.role,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
        },
        accessToken: response.accessToken,
      };
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  },

  /**
   * Register new user with Cognito
   */
  register: async (userData: RegisterData) => {
    // Registration MUST go through the backend, not straight to Cognito.
    // POST /auth/register is what writes the USER#PROFILE row in DynamoDB and
    // records Terms/Privacy consent; AuthService.login then requires that row.
    // Calling cognitoSignUp() directly (as this used to) produced a Cognito
    // identity with no profile — an account that could never log in and whose
    // consent was never captured (DEF-002).
    return makeSecureRequest<{ userId: string; email: string; message: string }>({
      method: 'POST',
      path: '/api/v1/auth/register',
      body: {
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role ?? 'BUYER',
        acceptedTerms: userData.acceptedTerms,
        acceptedPrivacy: userData.acceptedPrivacy,
      },
      requiresAuth: false,
      requiresHmac: false,
    });
  },

  /**
   * Logout user — clears locally stored tokens
   */
  logout: async () => {
    try {
      clearTokens();
      return { success: true };
    } catch (error: any) {
      console.error('Logout error:', error);
      clearTokens();
      return { success: false };
    }
  },

  /**
   * Request password reset
   */
  forgotPassword: async (data: { email: string }) => {
    try {
      await cognitoForgotPassword(data.email);
      return {
        success: true,
        message: 'Password reset code sent to your email',
      };
    } catch (error: any) {
      throw new Error(error.message || 'Password reset request failed');
    }
  },

  /**
   * Reset password with code
   */
  resetPassword: async (data: { email: string; code: string; newPassword: string }) => {
    try {
      await cognitoConfirmPassword(data.email, data.code, data.newPassword);
      return {
        success: true,
        message: 'Password reset successful',
      };
    } catch (error: any) {
      throw new Error(error.message || 'Password reset failed');
    }
  },

  /**
   * Verify email with code
   */
  verifyEmail: async (data: { email: string; code: string }) => {
    try {
      await cognitoConfirmSignUp(data.email, data.code);
      return {
        success: true,
        message: 'Email verified successfully',
      };
    } catch (error: any) {
      throw new Error(error.message || 'Email verification failed');
    }
  },

  /**
   * Resend the sign-up verification code
   */
  resendConfirmation: async (data: { email: string }) => {
    try {
      await cognitoResendSignUp(data.email);
      return {
        success: true,
        message: 'A new verification code has been sent to your email',
      };
    } catch (error: any) {
      throw new Error(error.message || 'Could not resend the verification code');
    }
  },
};
